import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { CustomFieldType, CustomFieldValidation, FIELD_TYPE_CONFIGS } from '@/types/customField';

interface ValidationRuleEditorProps {
  fieldType: CustomFieldType;
  rules: CustomFieldValidation;
  onChange: (rules: CustomFieldValidation) => void;
  disabled?: boolean;
}

export const ValidationRuleEditor: React.FC<ValidationRuleEditorProps> = ({
  fieldType,
  rules,
  onChange,
  disabled = false,
}) => {
  const config = FIELD_TYPE_CONFIGS.find(c => c.type === fieldType);
  if (!config) return null;

  const { supportsValidation } = config;

  const handleChange = (key: keyof CustomFieldValidation, value: any) => {
    onChange({
      ...rules,
      [key]: value,
    });
  };

  return (
    <div className="space-y-4">
      {/* Required */}
      {supportsValidation.required && (
        <div className="flex items-center justify-between">
          <label className="text-sm text-gray-700">Required Field</label>
          <input
            type="checkbox"
            checked={rules.required || false}
            onChange={(e) => handleChange('required', e.target.checked)}
            disabled={disabled}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
        </div>
      )}

      {/* Min/Max for numbers */}
      {(supportsValidation.min || supportsValidation.max) && (
        <div className="grid grid-cols-2 gap-4">
          {supportsValidation.min && (
            <div>
              <label className="text-sm text-gray-700 block mb-1">
                Minimum Value
              </label>
              <Input
                type="number"
                value={rules.min ?? ''}
                onChange={(e) => handleChange('min', e.target.value ? Number(e.target.value) : undefined)}
                disabled={disabled}
                placeholder="No limit"
              />
            </div>
          )}
          {supportsValidation.max && (
            <div>
              <label className="text-sm text-gray-700 block mb-1">
                Maximum Value
              </label>
              <Input
                type="number"
                value={rules.max ?? ''}
                onChange={(e) => handleChange('max', e.target.value ? Number(e.target.value) : undefined)}
                disabled={disabled}
                placeholder="No limit"
              />
            </div>
          )}
        </div>
      )}

      {/* MinLength/MaxLength for text */}
      {(supportsValidation.minLength || supportsValidation.maxLength) && (
        <div className="grid grid-cols-2 gap-4">
          {supportsValidation.minLength && (
            <div>
              <label className="text-sm text-gray-700 block mb-1">
                Min Length
              </label>
              <Input
                type="number"
                min={0}
                value={rules.minLength ?? ''}
                onChange={(e) => handleChange('minLength', e.target.value ? Number(e.target.value) : undefined)}
                disabled={disabled}
                placeholder="No limit"
              />
            </div>
          )}
          {supportsValidation.maxLength && (
            <div>
              <label className="text-sm text-gray-700 block mb-1">
                Max Length
              </label>
              <Input
                type="number"
                min={0}
                value={rules.maxLength ?? ''}
                onChange={(e) => handleChange('maxLength', e.target.value ? Number(e.target.value) : undefined)}
                disabled={disabled}
                placeholder="No limit"
              />
            </div>
          )}
        </div>
      )}

      {/* Pattern for text */}
      {supportsValidation.pattern && (
        <div>
          <label className="text-sm text-gray-700 block mb-1">
            Validation Pattern (RegEx)
          </label>
          <Input
            value={rules.pattern || ''}
            onChange={(e) => handleChange('pattern', e.target.value || undefined)}
            disabled={disabled}
            placeholder="e.g., ^[A-Za-z]+$"
            className="font-mono text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            Regular expression pattern for validation
          </p>
        </div>
      )}

      {/* Validation Preview */}
      {Object.keys(rules).filter(k => rules[k as keyof CustomFieldValidation] !== undefined).length > 0 && (
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-blue-700 mb-2">
            <AlertCircle className="w-4 h-4" />
            <span className="font-medium">Validation Rules Active</span>
          </div>
          <ul className="text-xs text-blue-600 space-y-1">
            {rules.required && <li>Field is required</li>}
            {rules.min !== undefined && <li>Minimum value: {rules.min}</li>}
            {rules.max !== undefined && <li>Maximum value: {rules.max}</li>}
            {rules.minLength !== undefined && <li>Minimum length: {rules.minLength} characters</li>}
            {rules.maxLength !== undefined && <li>Maximum length: {rules.maxLength} characters</li>}
            {rules.pattern && <li>Must match pattern: {rules.pattern}</li>}
          </ul>
        </div>
      )}
    </div>
  );
};
